'use client';

import { useCallback, useMemo, useRef } from 'react';
import { isArchivedEntity } from '@terra/shared-dto-types';
import type { GroupedSignal } from '@terra/shared-contracts-signals';
import { useTabScopedSignalsFilters } from '../../../store/tab/signalsTabScopedFilterStore';
import {
  countSelectedMutableSignals,
  excludeArchivedSignals,
  selectMutableSignals,
} from '../../../utils/signal-archive-selection';

type SignalIdSetUpdate = Set<string> | ((previous: Set<string>) => Set<string>);

const toggleSignalId = ({
  signalIds,
  signalId,
  shouldInclude,
}: {
  signalIds: Set<string>;
  signalId: string;
  shouldInclude: boolean;
}) => {
  const nextSignalIds = new Set(signalIds);

  if (shouldInclude) {
    nextSignalIds.add(signalId);
  } else {
    nextSignalIds.delete(signalId);
  }

  return nextSignalIds;
};

function usePersistedSignalIdSet({
  ids,
  setIds,
}: {
  ids: string[];
  setIds: (ids: string[]) => void;
}) {
  const idSet = useMemo(() => new Set(ids), [ids]);
  const idSetRef = useRef(idSet);

  idSetRef.current = idSet;

  const update = useCallback(
    (next: SignalIdSetUpdate) => {
      const resolved =
        typeof next === 'function' ? next(idSetRef.current) : next;

      idSetRef.current = resolved;
      setIds(Array.from(resolved));
    },
    [setIds],
  );
  const reset = useCallback(() => {
    idSetRef.current = new Set();
  }, []);

  return useMemo(() => ({ idSet, update, reset }), [idSet, reset, update]);
}

export function useSignalsSelection({
  signals,
  selectableTotalCount,
  fetchAllSignals,
}: {
  signals: readonly GroupedSignal[];
  selectableTotalCount: number;
  fetchAllSignals: () => Promise<GroupedSignal[]>;
}) {
  const store = useTabScopedSignalsFilters();
  const { isAllFilteredSelected, setIsAllFilteredSelected, clearSelection } =
    store;
  const selected = usePersistedSignalIdSet({
    ids: store.selectedIds,
    setIds: store.setSelectedIds,
  });
  const excluded = usePersistedSignalIdSet({
    ids: store.excludedIds,
    setIds: store.setExcludedIds,
  });
  const selectedSignalIds = selected.idSet;
  const excludedSignalIds = excluded.idSet;
  const signalById = useMemo(
    () => new Map(signals.map((signal) => [signal.id, signal])),
    [signals],
  );
  const selectedCount = countSelectedMutableSignals({
    isAllFilteredSelected,
    selectableTotalCount,
    excludedCount: excludedSignalIds.size,
    selectedCount: selectedSignalIds.size,
  });
  const areAllSelected =
    isAllFilteredSelected &&
    excludedSignalIds.size === 0 &&
    selectableTotalCount > 0;
  const clear = useCallback(() => {
    clearSelection();
    selected.reset();
    excluded.reset();
  }, [clearSelection, excluded, selected]);
  const selectAll = useCallback(
    (shouldSelect: boolean) => {
      if (!shouldSelect) {
        clear();

        return;
      }

      setIsAllFilteredSelected(true);
      selected.update(new Set());
      excluded.update(new Set());
    },
    [clear, excluded, selected, setIsAllFilteredSelected],
  );
  const selectSignal = useCallback(
    ({
      signalId,
      shouldSelect,
    }: {
      signalId: string;
      shouldSelect: boolean;
    }) => {
      if (isArchivedEntity(signalById.get(signalId))) return;

      if (isAllFilteredSelected) {
        excluded.update((previous) =>
          toggleSignalId({
            signalIds: previous,
            signalId,
            shouldInclude: !shouldSelect,
          }),
        );

        return;
      }

      selected.update((previous) =>
        toggleSignalId({
          signalIds: previous,
          signalId,
          shouldInclude: shouldSelect,
        }),
      );
    },
    [excluded, isAllFilteredSelected, selected, signalById],
  );
  const isSignalSelected = useCallback(
    (signal: GroupedSignal) =>
      !isArchivedEntity(signal) &&
      (isAllFilteredSelected
        ? !excludedSignalIds.has(signal.id)
        : selectedSignalIds.has(signal.id)),
    [excludedSignalIds, isAllFilteredSelected, selectedSignalIds],
  );
  const getSelectedSignals = useCallback(async (): Promise<GroupedSignal[]> => {
    if (selectedCount === 0) {
      return [];
    }

    if (!isAllFilteredSelected) {
      const loadedSelection = excludeArchivedSignals(
        Array.from(selectedSignalIds)
          .map((signalId) => signalById.get(signalId))
          .filter((signal): signal is GroupedSignal => Boolean(signal)),
      );

      if (loadedSelection.length === selectedSignalIds.size) {
        return loadedSelection;
      }
    }

    return selectMutableSignals({
      signals: await fetchAllSignals(),
      excludedIds: excludedSignalIds,
      selectedIds: selectedSignalIds,
      isAllFilteredSelected,
    });
  }, [
    excludedSignalIds,
    fetchAllSignals,
    isAllFilteredSelected,
    selectedCount,
    selectedSignalIds,
    signalById,
  ]);

  return {
    selectedSignalIds,
    isAllFilteredSelected,
    selectedCount,
    areAllSelected,
    hasPartialSelection: selectedCount > 0 && !areAllSelected,
    signalById,
    selectAll,
    selectSignal,
    isSignalSelected,
    getSelectedSignals,
    clear,
  };
}

export type SignalsSelection = ReturnType<typeof useSignalsSelection>;
